import { useMemo, useState } from 'react';
import { formatDate, formatMoney } from '../../../lib/format';

const FILTERS = [
  { value: 'all', label: 'Todas', field: null },
  { value: 'create', label: 'A crear', field: 'toCreate' },
  { value: 'update', label: 'A actualizar', field: 'toUpdate' },
  { value: 'skip', label: 'Sin cambios', field: 'unchanged' },
  { value: 'error', label: 'Errores', field: 'errors' },
];

const STATUS_BADGE = {
  create: 'gym-badge gym-badge--success',
  update: 'gym-badge gym-badge--info',
  skip: 'gym-badge gym-badge--neutral',
  error: 'gym-badge gym-badge--danger',
};

const STATUS_LABEL = {
  create: 'A crear', update: 'A actualizar', skip: 'Sin cambios', error: 'Error',
};

function quotaPeriod(quota) {
  // `endDate` is EXCLUSIVE (member-import-service.js's own convention, same
  // one `GET /members/export` already settled on): shown as-is, never
  // recomputed into an inclusive "last day" on the client.
  return `${formatDate(quota.startDate)} – ${formatDate(quota.endDate)} (sin incluir)`;
}

/**
 * Step 3 of the import wizard (plan task E6): a read-only analysis of what
 * `POST /members/import/commit` would do, from `POST .../preview`. Every
 * count, row and plan-to-create comes straight from that response — this
 * only filters `rows` locally by status (same "fetch once, filter in memory"
 * pattern as `MembersPage`) and formats the quota DTO for display; it never
 * recomputes a status, a balance or a date itself.
 *
 * Toggling "Actualizar socios existentes" re-runs the preview through the
 * parent (`onToggleUpdateExisting`) with the same mapping, since whether an
 * existing member becomes `update` or `skip` is the server's call, not
 * something this component can derive from the previous response.
 */
export function PreviewStep({
  summary, rows, plansToCreate, notImportedColumns, updateExisting, onToggleUpdateExisting,
  onBack, onConfirm, loading, error,
}) {
  const [filter, setFilter] = useState('all');

  const visibleRows = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((row) => row.status === filter);
  }, [rows, filter]);

  return (
    <div className="gym-import-preview">
      {error && <p role="alert" className="gym-form__error">{error}</p>}

      <dl className="gym-metrics gym-import-preview__summary" data-testid="gym-import-summary">
        <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--create">
          <dt className="gym-metric-card__label">A crear</dt>
          <dd className="gym-metric-card__value">{summary.toCreate}</dd>
        </div>
        <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--update">
          <dt className="gym-metric-card__label">A actualizar</dt>
          <dd className="gym-metric-card__value">{summary.toUpdate}</dd>
        </div>
        <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--unchanged">
          <dt className="gym-metric-card__label">Sin cambios</dt>
          <dd className="gym-metric-card__value">{summary.unchanged}</dd>
        </div>
        <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--error">
          <dt className="gym-metric-card__label">Con errores</dt>
          <dd className="gym-metric-card__value">{summary.errors}</dd>
        </div>
      </dl>

      {plansToCreate && plansToCreate.length > 0 && (
        <p className="gym-muted-text">
          Se van a crear {plansToCreate.length} planes nuevos:{' '}
          {plansToCreate.map((plan) => `${plan.name} (${formatMoney(plan.price)})`).join(', ')}.
        </p>
      )}

      {notImportedColumns && notImportedColumns.length > 0 && (
        <p className="gym-muted-text">Columnas no importadas: {notImportedColumns.join(', ')}.</p>
      )}

      <label className="gym-import-preview__toggle">
        <input
          type="checkbox"
          checked={updateExisting}
          disabled={loading}
          onChange={(event) => onToggleUpdateExisting(event.target.checked)}
        />
        Actualizar socios existentes (nombre, DNI y teléfono; nunca el email)
      </label>

      {loading && (
        <p role="status" aria-live="polite" className="gym-import-status">
          <span className="gym-state__spinner" aria-hidden="true" />
          Actualizando vista previa…
        </p>
      )}

      <div className="gym-import-preview__filters" role="group" aria-label="Filtrar por estado">
        {FILTERS.map(({ value, label, field }) => (
          <button
            key={value}
            type="button"
            className={filter === value ? 'gym-admin-filter gym-admin-filter--active' : 'gym-admin-filter'}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {label}{field ? ` (${summary[field]})` : ''}
          </button>
        ))}
      </div>

      <div className="gym-import-table-wrapper">
        <table className="gym-table gym-import-preview-table">
          <caption className="gym-sr-only">Vista previa de la importación</caption>
          <thead>
            <tr>
              <th scope="col">Fila</th>
              <th scope="col">Nombre</th>
              <th scope="col">Email</th>
              <th scope="col">DNI</th>
              <th scope="col">Teléfono</th>
              <th scope="col">Estado</th>
              <th scope="col">Plan</th>
              <th scope="col">Período</th>
              <th scope="col">Pagado</th>
              <th scope="col">Saldo</th>
              <th scope="col">Observaciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.row}>
                <td data-label="Fila">{row.row}</td>
                <td data-label="Nombre">{row.name}</td>
                <td data-label="Email">{row.email}</td>
                <td data-label="DNI">{row.documentNumber || '—'}</td>
                <td data-label="Teléfono">{row.phone || '—'}</td>
                <td data-label="Estado"><span className={STATUS_BADGE[row.status]}>{STATUS_LABEL[row.status] || row.status}</span></td>
                <td data-label="Plan">{row.quota ? row.quota.planName : '—'}</td>
                <td data-label="Período">{row.quota ? quotaPeriod(row.quota) : '—'}</td>
                <td data-label="Pagado">{row.quota ? formatMoney(row.quota.paid) : '—'}</td>
                <td data-label="Saldo">{row.quota ? formatMoney(row.quota.balance) : '—'}</td>
                <td data-label="Observaciones">{row.message || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="gym-admin-form-actions">
        <button type="button" className="gym-button gym-button--secondary" disabled={loading} onClick={onBack}>Volver a mapear</button>
        <button type="button" className="gym-button gym-button--primary" disabled={loading} onClick={onConfirm}>Confirmar importación</button>
      </div>
    </div>
  );
}
